import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const OPTIONS = [["available", "Available"], ["modified", "Modified"], ["unavailable", "Unavailable"]];
const ALLOWED = ["admin", "owner", "coach", "physio"];

/** Availability: set by Admin, Head Coach or Physio only. Shown in the workspace header. */
export default function AvailabilityControl({ athlete, onChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(athlete?.availability || "available");
  const [reason, setReason] = useState(athlete?.availability_reason || "");
  const [until, setUntil] = useState(athlete?.availability_until || "");
  const canEdit = ALLOWED.includes(user?.role);
  const current = athlete?.availability || "available";
  const label = current.replace(/_/g, " ");
  const color = current === "unavailable" ? "text-traffic-red border-traffic-red" : (current === "modified" || current === "at_risk") ? "text-traffic-amber border-traffic-amber" : "text-muted-foreground border-border";

  const save = async () => {
    try {
      await api.post(`/athletes/${athlete.athlete_id}/availability`, { availability: value, reason, until });
      toast.success(`Availability set to ${value}`);
      setOpen(false); onChange?.();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not set availability");
    }
  };

  return (
    <div className="relative">
      <button data-testid="availability-btn" onClick={() => canEdit && setOpen((v) => !v)}
        title={canEdit ? "Set availability" : "Only an admin, head coach or physio can change this"}
        className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${color} ${canEdit ? "hover:bg-surface" : "cursor-default"} transition-colors`}>
        {label}{athlete?.availability_reason ? ` · ${athlete.availability_reason}` : ""}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-64 max-w-[85vw] border border-border bg-background shadow-lg p-3 space-y-2" data-testid="availability-menu">
          <select data-testid="availability-select" value={value} onChange={(e) => setValue(e.target.value)} className="inp text-sm">
            {OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-testid="availability-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Short reason (optional)" className="inp text-sm" />
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground">Expected return
            <input data-testid="availability-until" type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="inp text-sm mt-1" />
          </label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 border border-border hover:bg-surface transition-colors">Cancel</button>
            <button data-testid="availability-save" onClick={save} className="text-xs px-3 py-1.5 bg-foreground text-background hover:bg-neutral-800 transition-colors">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
